/**
 * The things that tell you what a room is.
 *
 * A validated greybox is a correct building and an empty one. What is missing
 * is not detail — a wall with more bevels on it is still a wall — it is
 * evidence of use: shelving because something is stored here, a cable tray
 * because power has to get to the far end, a sign because somebody had to find
 * their way. Those are the objects a person reads a place from, and none of
 * them can be derived from geometry alone. They come from what the space is
 * *for*, which `place.ts` works out.
 *
 * Everything here is placed against the compiled geometry rather than at
 * coordinates somebody typed. A boundary run with a space on one side is a
 * wall you can stand something against; the cells of that space that are not
 * beside a wall are floor you can put something in the middle of; an opening
 * is a gap nothing may block. So the dressing follows the building rather than
 * being laid over the top of it, and a map that changes shape re-dresses
 * itself.
 *
 * None of it collides. The compiler owns what you can walk into and what you
 * can shoot through, and a decorative crate that stops a bullet would be a
 * decorative crate that the validator never gated. It is all merged into two
 * draw calls — one lit, one for the signs — and kept to a budget, because this
 * is a phone game and clutter is exactly the sort of thing that quietly costs
 * ten thousand triangles a room.
 */

import * as THREE from 'three';
import { accentFor } from './materials.ts';
import { profileFor, type Language } from './art.ts';

/**
 * The one map-scale thing each kind of building gets.
 *
 * Keyed off the map's declared language rather than off a room's use, because
 * this is a statement about the whole floor: a transit hall has an overhead
 * concourse, a dig has light coming down into it, a laboratory has a wall that
 * is a system. One per map means it is the thing you remember about the map.
 */
type Hero =
  | 'lightwell'
  | 'bridge'
  | 'louver_wall'
  | 'hanging_volume'
  | 'cantilever'
  | 'reflecting_pool';

const HERO: Record<Language, Hero> = {
  excavation: 'lightwell',
  transit: 'bridge',
  laboratory: 'louver_wall',
  industrial: 'hanging_volume',
  civic: 'reflecting_pool',
  rooftop: 'cantilever',
};

function languageOf(id: string): Language {
  return profileFor(id).language;
}
import type { CompiledLevel, LevelSpec } from 'levelspec';
import type { Place, PlaceUse } from './place.ts';

const OUTSIDE = '__outside__';

/**
 * Set while a geometry check is running: every box drawn is recorded with the
 * name of the fixture that drew it. Off in the game, where it would be a few
 * thousand objects a floor for nobody.
 */
export interface TracedBox {
  fixture: string;
  /** Which placement of that fixture drew it; boxes of one fixture share it. */
  at: number;
  /** The triangles it emitted, as a range into the merged mesh. */
  tri0: number;
  tri1: number;
  /**
   * A cylinder rather than a box.
   *
   * Its bounds are the box the cylinder is inscribed in, which touches the
   * surface along four lines and is everywhere else outside it. So two of
   * these sharing a bound share a tangent, not a face.
   */
  round: boolean;
  min: [number, number, number];
  max: [number, number, number];
}
/** A world-space box: what a whole fixture occupies, faces and all. */
interface Extent {
  x0: number; y0: number; z0: number;
  x1: number; y1: number; z1: number;
}

let trace: TracedBox[] | null = null;
export function traceProps<T>(run: () => T): { result: T; boxes: TracedBox[] } {
  trace = [];
  try {
    const result = run();
    return { result, boxes: trace };
  } finally {
    trace = null;
  }
}

/** Where something can stand, and which way it faces. */
interface Socket {
  /** Canonical world position of the base. */
  x: number;
  y: number;
  z: number;
  /** Unit vector into the room, canonical (x, y). */
  nx: number;
  ny: number;
  /** Space along the wall available here, in metres. */
  room: number;
  place: Place;
}

function rng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13; h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5; h >>>= 0;
    return h / 4294967296;
  };
}

// -----------------------------------------------------------------------------
// The recipes
//
// One line per fixture: what to place, how many per hundred square metres of
// floor, and whether it goes against a wall or out in the open. Written per
// use rather than per map, so a boiler room in a Tokyo tower and a boiler room
// in a Moroccan market are both boiler rooms.

type Fixture =
  | 'rack' | 'crates' | 'pallet' | 'barrels' | 'bottles' | 'sacks' | 'tyres'
  | 'pipes' | 'tray' | 'duct' | 'fusebox' | 'tank' | 'lockers' | 'bench'
  | 'desk' | 'cabinet' | 'table' | 'servery' | 'noticeboard' | 'stall'
  | 'planter' | 'bollard' | 'barrier' | 'bin' | 'trolley' | 'fire' | 'lamp'
  | 'vending' | 'shelf' | 'workbench' | 'sign'
  // Small things a street collects. Placed by the litter pass, not by a
  // composition — see `LITTER`.
  | 'chair' | 'cans' | 'cone' | 'carton'
  // Things that live on the outside of a building, facing a street.
  | 'downpipe' | 'aircon' | 'awning' | 'shutter' | 'meter' | 'dish' | 'grille'
  | 'cables' | 'standpipe' | 'washing';

interface Recipe {
  fixture: Fixture;
  /** Per 100 m² of floor. */
  per100: number;
  where: 'wall' | 'open' | 'corner';
}

/**
 * What a room is made of, as a cast rather than a density.
 *
 * The old table said how many of each fixture belonged per hundred square
 * metres, and a large room therefore got a large number of small things: a
 * concourse came out with benches, bins, vending machines, noticeboards, lamps
 * and planters, six kinds of object at roughly the same size and roughly the
 * same importance, none of which you would remember five seconds after leaving.
 *
 * A room is one thing you remember and a few things you do not. So each use
 * names a hero — the mass that gives the room its silhouette — and at most
 * three supports, and every one of them is placed once no matter how big the
 * floor is. What a bigger room gets is not more objects. It gets more room.
 */
interface Composition {
  /** The one mass the room is remembered by. Placed first, given the best spot. */
  hero: Recipe;
  /** At most three, once each. */
  supports: Recipe[];
}

const COMPOSE: Record<PlaceUse, Composition> = {
  concourse: {
    hero: { fixture: 'vending', per100: 0, where: 'wall' },
    supports: [
      { fixture: 'bench', per100: 0, where: 'open' },
      { fixture: 'bin', per100: 0, where: 'wall' },
    ],
  },
  hall: {
    hero: { fixture: 'cabinet', per100: 0, where: 'wall' },
    supports: [{ fixture: 'bench', per100: 0, where: 'wall' }],
  },
  office: {
    hero: { fixture: 'desk', per100: 0, where: 'open' },
    supports: [
      { fixture: 'cabinet', per100: 0, where: 'wall' },
      { fixture: 'bin', per100: 0, where: 'wall' },
    ],
  },
  workshop: {
    hero: { fixture: 'workbench', per100: 0, where: 'wall' },
    supports: [
      { fixture: 'rack', per100: 0, where: 'wall' },
      { fixture: 'barrels', per100: 0, where: 'corner' },
    ],
  },
  store: {
    hero: { fixture: 'shelf', per100: 0, where: 'wall' },
    supports: [{ fixture: 'crates', per100: 0, where: 'corner' }],
  },
  plant: {
    hero: { fixture: 'tank', per100: 0, where: 'open' },
    supports: [
      { fixture: 'pipes', per100: 0, where: 'wall' },
      { fixture: 'fusebox', per100: 0, where: 'wall' },
    ],
  },
  yard: {
    hero: { fixture: 'barrier', per100: 0, where: 'open' },
    supports: [{ fixture: 'pallet', per100: 0, where: 'corner' }],
  },
  market: {
    hero: { fixture: 'stall', per100: 0, where: 'open' },
    supports: [{ fixture: 'crates', per100: 0, where: 'corner' }],
  },
  tunnel: {
    hero: { fixture: 'duct', per100: 0, where: 'wall' },
    supports: [{ fixture: 'cables', per100: 0, where: 'wall' }],
  },
  stair: {
    hero: { fixture: 'lamp', per100: 0, where: 'wall' },
    supports: [],
  },
  terrace: {
    hero: { fixture: 'planter', per100: 0, where: 'corner' },
    supports: [{ fixture: 'bench', per100: 0, where: 'open' }],
  },
  bay: {
    hero: { fixture: 'crates', per100: 0, where: 'wall' },
    supports: [
      { fixture: 'pallet', per100: 0, where: 'corner' },
      { fixture: 'trolley', per100: 0, where: 'open' },
    ],
  },
  checkpoint: {
    hero: { fixture: 'barrier', per100: 0, where: 'open' },
    supports: [{ fixture: 'sign', per100: 0, where: 'wall' }],
  },
  archive: {
    hero: { fixture: 'shelf', per100: 0, where: 'wall' },
    supports: [{ fixture: 'lockers', per100: 0, where: 'wall' }],
  },
  garage: {
    hero: { fixture: 'workbench', per100: 0, where: 'wall' },
    supports: [
      { fixture: 'tyres', per100: 0, where: 'corner' },
      { fixture: 'barrels', per100: 0, where: 'corner' },
    ],
  },
  canteen: {
    hero: { fixture: 'servery', per100: 0, where: 'wall' },
    supports: [
      { fixture: 'table', per100: 0, where: 'open' },
      { fixture: 'noticeboard', per100: 0, where: 'wall' },
    ],
  },
};

/**
 * How many supports a room of this size is entitled to.
 *
 * Nought to three, and a small room gets nought: the negative space is not
 * what is left over after the furniture, it is the thing being composed.
 */
function supportCount(area: number): number {
  if (area < 30) return 0;
  if (area < 90) return 1;
  if (area < 220) return 2;
  return 3;
}

/** Every place gets its name on the wall; some get a bit more said about them. */
const NOTICES: Record<PlaceUse, string[]> = {
  concourse: ['WAY OUT', 'NO LOITERING'],
  hall: ['QUIET PLEASE'],
  office: ['STAFF ONLY', 'NO ENTRY'],
  workshop: ['EYE PROTECTION', 'MIND THE STEP'],
  store: ['STOCK CONTROL', 'FIFO'],
  plant: ['HIGH VOLTAGE', 'NO NAKED FLAME', 'AUTHORISED ONLY'],
  yard: ['KEEP CLEAR', 'NO PARKING'],
  market: ['MARKET HOURS', 'CASH ONLY'],
  tunnel: ['SERVICE ACCESS', 'DUCK'],
  stair: ['FIRE EXIT', 'HOLD THE RAIL'],
  terrace: ['ROOF ACCESS', 'FALL HAZARD'],
  bay: ['LOADING ONLY', 'REVERSING VEHICLES'],
  checkpoint: ['STOP', 'SHOW PASS'],
  archive: ['NO FOOD OR DRINK'],
  garage: ['NO SMOKING', 'OIL DISPOSAL'],
  canteen: ['WASH YOUR HANDS', 'TODAY: STEW'],
};

// -----------------------------------------------------------------------------

export interface Props {
  group: THREE.Group;
  /**
   * The box every placed fixture actually occupies, in world space.
   *
   * Measured off the vertices emitted rather than predicted from the recipe,
   * which is the same reason the placement pass trusts them to keep two
   * fixtures out of each other. `world.ts` turns them into colliders.
   */
  solids: { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }[];
  triangles: number;
  dispose(): void;
}

/**
 * Dress a compiled level.
 *
 * `budget` is a triangle ceiling for the whole set. Recipes are applied in
 * order of how much they say about the place, so running out means losing the
 * fourth crate in a store room rather than losing the sign over the door.
 */
export function buildProps(
  spec: LevelSpec,
  level: CompiledLevel,
  places: Place[],
  seed: string,
  budget = 42000,
): Props {
  const group = new THREE.Group();
  const rand = rng(`${seed}:props`);
  const g = spec.grid ?? 1;
  const wt = spec.wall_thickness ?? 0.22;
  /*
   * Which place a cell belongs to.
   *
   * Not a lookup by space id: a big space is cut into several places, and a
   * boundary run still names the space it was drawn between. Going through the
   * cells is the only thing that knows which zone of a lane a given stretch of
   * wall is standing in.
   */
  const byCell = new Map<string, Place>();
  for (const p of places) for (const k of p.cells) byCell.set(`${p.layer}|${k}`, p);
  const placeAtCell = (layer: string, cx: number, cy: number): Place | undefined =>
    byCell.get(`${layer}|${cx},${cy}`);

  // ---- the buffers ---------------------------------------------------------
  const pos: number[] = [];
  const nrm: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  let tris = 0;

  /** Current placement frame: origin, and the direction into the room. */
  let ox = 0, oy = 0, oz = 0, fx = 0, fy = 1;
  /*
   * Which fixture is being drawn, for the geometry checks.
   *
   * Everything ends up in one buffer, so a fault found in the merged mesh has
   * no name attached to it. This keeps one, and only when something is asking.
   */
  let drawing = '';
  /*
   * The world box of the fixture being drawn, while one is being measured.
   *
   * A fixture is drawn out of its own origin, not centred on it, and half of
   * them reach further forward than sideways. Spacing them by a circle around
   * the socket was therefore always an estimate, and two fixtures that each
   * cleared the estimate could still land in one another.
   */
  let extent: Extent | null = null;
  /** Which placement is being drawn: boxes of one fixture may overlap, two may not. */
  let instance = 0;
  const grow = (p: [number, number, number]): void => {
    if (!extent) return;
    if (p[0] < extent.x0) extent.x0 = p[0];
    if (p[0] > extent.x1) extent.x1 = p[0];
    if (p[1] < extent.y0) extent.y0 = p[1];
    if (p[1] > extent.y1) extent.y1 = p[1];
    if (p[2] < extent.z0) extent.z0 = p[2];
    if (p[2] > extent.z1) extent.z1 = p[2];
  };
  /** How tall the wall a facade fitting is going on. */
  let facadeHeight = 6;

  /** Local (right, up, out) to canonical (x, y, z). */
  const put = (r: number, u: number, o: number): [number, number, number] => [
    ox + fy * r + fx * o,
    oy - fx * r + fy * o,
    oz + u,
  ];

  /**
   * A box in the placement frame: right/up/out, sizes, colour.
   *
   * Canonical space is Z-up; the renderer wants Three's Y-up, so the flip
   * happens once here — (x, y, z) becomes (x, z, -y) — rather than in every
   * fixture.
   */
  const box = (
    r0: number, u0: number, o0: number,
    r1: number, u1: number, o1: number,
    c: THREE.Color,
  ): void => {
    // Lowest corner first on every axis, so "the +right face" really is the
    // one on the right. A fixture that stacks downwards writes its bounds the
    // other way round, and that alone turned a box inside out.
    if (r0 > r1) [r0, r1] = [r1, r0];
    if (u0 > u1) [u0, u1] = [u1, u0];
    if (o0 > o1) [o0, o1] = [o1, o0];
    if (extent) {
      grow(put(r0, u0, o0));
      grow(put(r1, u1, o1));
    }
    const tri0 = tris;
    const corners: [number, number, number][] = [];
    for (const o of [o0, o1]) for (const u of [u0, u1]) for (const r of [r0, r1]) corners.push(put(r, u, o));
    // Index order above is r fastest, then u, then o.
    const V = (i: number): [number, number, number] => {
      const p = corners[i];
      return [p[0], p[2], -p[1]];
    };
    /**
     * One face, wound so that it faces the way it is meant to.
     *
     * The winding is measured against the direction the face is supposed to
     * look, exactly as the cylinders do it. Deriving the normal from the
     * winding instead — which is what this did — makes the two agree by
     * construction and says nothing at all about which side of the box the
     * face is showing: a box could be built entirely inside out and every
     * triangle in it would still report a normal it agreed with.
     */
    const quad = (
      a: number, b: number, c2: number, d: number,
      outward: [number, number, number],
    ): void => {
      const start = pos.length / 3;
      const pa = V(a), pb = V(b), pc = V(c2), pd = V(d);
      const e1 = [pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]];
      const e2 = [pc[0] - pa[0], pc[1] - pa[1], pc[2] - pa[2]];
      const cr = [
        e1[1] * e2[2] - e1[2] * e2[1],
        e1[2] * e2[0] - e1[0] * e2[2],
        e1[0] * e2[1] - e1[1] * e2[0],
      ];
      const flip = cr[0] * outward[0] + cr[1] * outward[1] + cr[2] * outward[2] < 0;
      const len = Math.hypot(outward[0], outward[1], outward[2]) || 1;
      for (const p of [pa, pb, pc, pd]) {
        pos.push(p[0], p[1], p[2]);
        nrm.push(outward[0] / len, outward[1] / len, outward[2] / len);
        col.push(c.r, c.g, c.b);
      }
      if (flip) idx.push(start, start + 2, start + 1, start, start + 3, start + 2);
      else idx.push(start, start + 1, start + 2, start, start + 2, start + 3);
      tris += 2;
    };
    quad(0, 2, 3, 1, dirIn(0, 0, -1));  // -out
    quad(4, 5, 7, 6, dirIn(0, 0, 1));   // +out
    quad(0, 1, 5, 4, dirIn(0, -1, 0));  // -up
    quad(2, 6, 7, 3, dirIn(0, 1, 0));   // +up
    quad(0, 4, 6, 2, dirIn(-1, 0, 0));  // -right
    quad(1, 3, 7, 5, dirIn(1, 0, 0));   // +right
    if (trace) {
      const a = put(r0, u0, o0);
      const b = put(r1, u1, o1);
      trace.push({
        fixture: drawing,
        at: instance,
        min: [Math.min(a[0], b[0]), Math.min(a[2], b[2]), Math.min(-a[1], -b[1])],
        max: [Math.max(a[0], b[0]), Math.max(a[2], b[2]), Math.max(-a[1], -b[1])],
        tri0,
        tri1: tris,
        round: false,
      });
    }
  };

  /** An upright cylinder in the placement frame. */
  /**
   * A direction in the placement frame, in world terms.
   *
   * `put` is a rotation and a translation; this is the same rotation without
   * the translation, which is what an outward normal needs. Two of the four
   * placement frames are mirrored — a fixture against a wall facing -x is the
   * same fixture facing +x reflected — and a mirrored frame reverses the
   * winding of everything drawn in it. That is why the facing is measured
   * rather than assumed, here and in `box`.
   */
  const dirIn = (r: number, u: number, o: number): [number, number, number] => [
    fy * r + fx * o,
    u,
    fx * r - fy * o,
  ];

  /** An upright cylinder in the placement frame. */
  const post = (r: number, o: number, radius: number, u0: number, u1: number, c: THREE.Color, sides = 7): void => {
    if (extent) {
      for (const u of [u0, u1])
        for (const rr of [r - radius, r + radius])
          for (const oo of [o - radius, o + radius]) grow(put(rr, u, oo));
    }
    const tri0 = tris;
    const ring: [number, number][] = [];
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2;
      ring.push([r + Math.cos(a) * radius, o + Math.sin(a) * radius]);
    }
    const V = (p: [number, number, number]): [number, number, number] => [p[0], p[2], -p[1]];

    /** One polygon, wound so that it faces the way it is meant to. */
    const facet = (
      corners: [number, number, number][],
      outward: [number, number, number],
    ): void => {
      const world = corners.map((q) => V(put(q[0], q[1], q[2])));
      const e1 = [world[1][0] - world[0][0], world[1][1] - world[0][1], world[1][2] - world[0][2]];
      const e2 = [world[2][0] - world[0][0], world[2][1] - world[0][1], world[2][2] - world[0][2]];
      const cr = [
        e1[1] * e2[2] - e1[2] * e2[1],
        e1[2] * e2[0] - e1[0] * e2[2],
        e1[0] * e2[1] - e1[1] * e2[0],
      ];
      const flip = cr[0] * outward[0] + cr[1] * outward[1] + cr[2] * outward[2] < 0;
      const len = Math.hypot(outward[0], outward[1], outward[2]) || 1;
      const start = pos.length / 3;
      for (const w of world) {
        pos.push(w[0], w[1], w[2]);
        nrm.push(outward[0] / len, outward[1] / len, outward[2] / len);
        col.push(c.r, c.g, c.b);
      }
      for (let i = 1; i + 1 < world.length; i++) {
        if (flip) idx.push(start, start + i + 1, start + i);
        else idx.push(start, start + i, start + i + 1);
        tris++;
      }
    };

    for (let i = 0; i < sides; i++) {
      const [ar, ao] = ring[i];
      const [br, bo] = ring[(i + 1) % sides];
      // Out from the axis through the middle of this facet.
      const mr = (ar + br) / 2 - r;
      const mo = (ao + bo) / 2 - o;
      facet(
        [[ar, u0, ao], [br, u0, bo], [br, u1, bo], [ar, u1, ao]],
        dirIn(mr, 0, mo),
      );
    }
    // Caps, so a drum seen from a gantry is not a tube. `u` is world height
    // whatever the frame does, so the outward direction is simply up or down.
    for (const [u, dir] of [[u0, -1], [u1, 1]] as [number, number][]) {
      const rim: [number, number, number][] = ring.map(([rr, oo]) => [rr, u, oo]);
      facet(dir > 0 ? rim : rim.slice().reverse(), [0, dir, 0]);
    }
    if (trace) {
      const min: [number, number, number] = [Infinity, Infinity, Infinity];
      const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
      for (const u of [u0, u1])
        for (const [rr, oo] of ring) {
          const q = V(put(rr, u, oo));
          for (let k = 0; k < 3; k++) {
            if (q[k] < min[k]) min[k] = q[k];
            if (q[k] > max[k]) max[k] = q[k];
          }
        }
      trace.push({ fixture: drawing, at: instance, min, max, tri0, tri1: tris, round: true });
    }
  };

  // ---- the palette ---------------------------------------------------------
  /*
   * The props have their own palette, and it is not the wall's.
   *
   * A first version tinted everything toward the map's masonry so it would sit
   * in the scheme, and the result was a beige pipe on a beige wall: correct,
   * invisible, and worth nothing. Fittings are made of different stuff from
   * buildings — galvanised steel, painted metal, plastic, timber — and the
   * point of putting them there is that you can see them. They take a fifth of
   * the map's colour so they belong to it, and no more.
   */
  /*
   * Three colours, and one of them is rationed.
   *
   * There were six — galvanised steel, painted metal, plastic, timber, rust
   * and the map's accent — each with a random lightness on top, which is how a
   * room ended up with several hundred small differently-coloured events and
   * no large one. Fittings now come from the same vocabulary as everything
   * else: graphite for the made and mechanical, pale grey for the few things
   * that are neither, and the map's single accent, which almost nothing here
   * is entitled to use.
   */
  /** The shell colour, for the handful of fittings that are part of the wall. */
  const base = new THREE.Color(0xe2e6e8);
  /*
   * Dark, but never a hole.
   *
   * Both of these were several shades lower. Against a lit exterior shell that
   * reads as machined metal; inside an enclosed band lit only by its own
   * building it reads as nothing at all — a fixture at four per cent of the
   * range in a room whose walls sit at thirty-five is not a dark object, it is
   * a silhouette with no surface, and two of them standing beside a hostile is
   * most of why the authored maps measured three quarters graphite.
   *
   * A dark that goes to zero the moment the light does is not a colour, it is
   * an absence. These keep enough value to be a material in a dim room and are
   * still unmistakably the dark family in a bright one.
   */
  const graphite = new THREE.Color(0x3b444e);
  const dark = new THREE.Color(0x232a32);
  const grey = new THREE.Color(0xb4bdc5);
  const accent = new THREE.Color(accentFor(seed));
  const metal = graphite;
  const wood = grey;
  const plastic = new THREE.Color(0x232a31);
  const rust = new THREE.Color(0x9aa4ad);
  /*
   * Painted metal, not the map's accent.
   *
   * `paint` reached a stall awning, a barrier, a rack of goods, a vending
   * front and a set of pipes, which is why a market lane measured eighteen per
   * cent accent: the colour that is supposed to mark one plane at the end of a
   * route was on a dozen objects standing in the middle of it. The accent now
   * has exactly one user — the panel below — and everything else that used to
   * be "the bright one" is pale grey against the graphite around it.
   *
   * That was written before it was true. Four fixtures kept it: a goods rack,
   * the tools on a workbench, the goods on a stall counter and a stake in a
   * planter, each alternating accent with something dark. None of them is
   * large and there are a great many of them, which is how a calavera came to
   * be 13.1% of one frame in amber against a budget of six — spread evenly
   * from the top of the picture to the bottom, because that is where the
   * fixtures are. They alternate pale against dark now, which is the contrast
   * the alternation was for and costs nothing from the palette.
   */
  const paint = grey;
  /*
   * No longer a jitter.
   *
   * Every fixture used to be multiplied by a random 0.86–1.14, so two crates
   * side by side were two different colours for no reason anyone could see or
   * name. Kept as a function because eighty fixtures call it, and because the
   * one place a variation would be legitimate — telling two large planes apart
   * — is not a place that draws crates.
   */
  const jitter = (c: THREE.Color): THREE.Color => c;

  // ---- fixtures ------------------------------------------------------------
  // Each one is written in its own local frame: `r` runs along the wall, `u`
  // is up, `o` is out into the room. Nothing here knows which way it is
  // facing, which is what makes the same crate work against all four walls.

  /**
   * Half the length of a fixture that runs along a wall.
   *
   * The sockets are spaced by the room each one was given, so a run drawn to
   * exactly that width tiles with its neighbours instead of lying on top of
   * one. Bounded below so a fixture in a doorway-sized gap is still a fixture.
   */
  const span = (w: number): number => Math.max(0.35, Math.min(3.5, w)) / 2;

  const FIXTURES: Record<Fixture, (w: number) => void> = {
    rack: (w) => {
      const half = Math.min(1.5, w / 2);
      const d = 0.5, h = 2.0 + rand() * 0.5;
      for (const r of [-half + 0.06, half - 0.06])
        for (const o of [0.08, d - 0.06])
          post(r, o, 0.045, 0, h, metal, 5);
      for (let i = 1; i <= 3; i++) {
        const u = (i / 3.6) * h;
        box(-half, u, 0.05, half, u + 0.05, d, jitter(metal));
        if (rand() < 0.7) {
          const bw = half * (0.4 + rand() * 0.5);
          box(-bw, u + 0.05, 0.1, bw, u + 0.05 + 0.3 + rand() * 0.25, d - 0.08, jitter(wood));
        }
      }
    },
    shelf: (w) => {
      // Freestanding, double-sided, an aisle's worth of it.
      const half = Math.min(1.8, w / 2);
      const h = 2.1;
      for (const o of [-0.3, 0.3]) {
        for (const r of [-half, half]) post(r, o, 0.04, 0, h, metal, 5);
        for (let i = 1; i <= 4; i++)
          box(-half, (i / 4.6) * h, o - 0.28, half, (i / 4.6) * h + 0.04, o + 0.28, jitter(metal));
      }
    },
    crates: () => {
      const n = 1 + Math.floor(rand() * 3);
      let u = 0;
      for (let i = 0; i < n; i++) {
        const s = 0.42 + rand() * 0.26;
        const r = (rand() - 0.5) * 0.3;
        box(r - s / 2, u, 0.08, r + s / 2, u + s, 0.08 + s, jitter(wood));
        // Stacked touching rather than sunk into each other: an overlap is two
        // solids in one place, which is the defect the compiler's own gates
        // reject in a level and should not be tolerated in the dressing.
        u += s;
      }
    },
    pallet: () => {
      box(-0.6, 0, 0.05, 0.6, 0.12, 1.05, jitter(wood));
      if (rand() < 0.7) {
        const h = 0.4 + rand() * 0.8;
        box(-0.52, 0.12, 0.12, 0.52, 0.12 + h, 0.98, jitter(rand() < 0.5 ? plastic : wood));
      }
    },
    barrels: () => {
      const n = 2 + Math.floor(rand() * 3);
      for (let i = 0; i < n; i++) {
        const r = -0.5 + (i / Math.max(1, n - 1)) * 1.0 + (rand() - 0.5) * 0.12;
        post(r, 0.45 + (rand() - 0.5) * 0.2, 0.29, 0, 0.88, jitter(i % 2 ? rust : paint), 9);
      }
    },
    bottles: () => {
      for (let i = 0; i < 4; i++)
        post(-0.36 + i * 0.24, 0.28, 0.1, 0, 1.25 + rand() * 0.1, jitter(i % 2 ? paint : metal), 7);
      box(-0.5, 0, 0.14, 0.5, 0.08, 0.42, dark);
    },
    sacks: () => {
      let u = 0;
      for (let i = 0; i < 3; i++) {
        const w2 = 0.36 + rand() * 0.1;
        box(-w2, u, 0.1, w2, u + 0.28, 0.1 + w2 * 1.4, jitter(wood.clone().lerp(base, 0.4)));
        u += 0.28;
      }
    },
    tyres: () => {
      for (let i = 0; i < 4; i++) post(0, 0.42, 0.36, i * 0.2, i * 0.2 + 0.18, dark, 9);
    },
    pipes: (w) => {
      // Exactly as long as the socket it was given, so the next one along
      // starts where this one stops. Drawn longer than their spacing, two runs
      // overlapped by half a metre at the same height on the same wall — which
      // is two surfaces on one plane facing one way, and no nudge fixes that.
      const half = span(w);
      const n = 2 + Math.floor(rand() * 2);
      for (let i = 0; i < n; i++) {
        const u = 2.1 + i * 0.24;
        const o = 0.16 + i * 0.02;
        // A pipe is a box run at this distance; the round one costs six times
        // as many triangles and reads the same from two metres away.
        box(-half, u, o, half, u + 0.13, o + 0.13, jitter(i % 2 ? metal : paint));
        // Shorter than the gap between runs. Drawn a centimetre taller, the
        // bracket of one run reached into the bracket of the next and the two
        // shared their side planes.
        // Spaced as a fraction of the run rather than by a fixed inset. On a
        // one-metre stretch the fixed version put all three clamps at zero —
        // three identical boxes in one place, which is every face coplanar
        // with every other.
        for (const t of [-0.72, 0, 0.72])
          box(t * half - 0.05, u - 0.05, o - 0.03, t * half + 0.05, u + 0.17, o + 0.16, dark);
      }
    },
    tray: (w) => {
      const half = span(w);
      const u = 2.45;
      // The lips stand *on* the floor of the tray rather than beside it. Drawn
      // from the same height they shared two planes with it, facing the same
      // way, which is z-fighting and is not something a nudge fixes.
      box(-half, u, 0.1, half, u + 0.04, 0.36, jitter(metal));
      box(-half, u + 0.04, 0.1, half, u + 0.11, 0.135, jitter(metal));
      box(-half, u + 0.04, 0.335, half, u + 0.11, 0.36, jitter(metal));
      // Inside the lips, not touching them: the last cable used to reach five
      // millimetres past the far lip, and both were sitting on the tray floor
      // at the same height.
      for (let i = 0; i < 3; i++)
        box(-half + 0.06, u + 0.04, 0.15 + i * 0.06, half - 0.06, u + 0.09, 0.195 + i * 0.06, jitter(grey));
    },
    duct: (w) => {
      const half = span(w);
      box(-half, 2.3, 0.12, half, 2.86, 0.68, jitter(metal));
      for (let i = -1; i <= 1; i++)
        box(i * 0.6 - 0.04, 2.28, 0.1, i * 0.6 + 0.04, 2.88, 0.7, dark);
    },
    fusebox: () => {
      box(-0.34, 1.05, 0.06, 0.34, 1.85, 0.24, jitter(metal));
      box(-0.3, 1.5, 0.24, 0.3, 1.66, 0.26, dark);
      box(-0.06, 1.1, 0.24, 0.06, 1.2, 0.27, grey);
    },
    tank: () => {
      post(0, 0.8, 0.72, 0, 2.4, jitter(metal), 11);
      box(-0.8, 0, 0.0, 0.8, 0.12, 1.6, dark);
      box(-0.1, 2.4, 0.7, 0.1, 2.9, 0.9, metal);
    },
    lockers: (w) => {
      const n = Math.max(2, Math.min(5, Math.floor(w / 0.42)));
      for (let i = 0; i < n; i++) {
        const r = (i - (n - 1) / 2) * 0.4;
        box(r - 0.19, 0, 0.06, r + 0.19, 1.85, 0.44, jitter(plastic));
        box(r - 0.06, 1.2, 0.44, r + 0.06, 1.28, 0.46, dark);
      }
    },
    bench: (w) => {
      const half = Math.min(0.95, w / 2);
      box(-half, 0.42, -0.22, half, 0.5, 0.22, jitter(wood));
      for (const r of [-half + 0.14, half - 0.14]) {
        box(r - 0.05, 0, -0.16, r + 0.05, 0.42, -0.06, metal);
        box(r - 0.05, 0, 0.06, r + 0.05, 0.42, 0.16, metal);
      }
    },
    desk: () => {
      box(-0.75, 0.7, -0.35, 0.75, 0.76, 0.35, jitter(wood));
      for (const r of [-0.68, 0.68]) box(r - 0.04, 0, -0.32, r + 0.04, 0.7, 0.32, metal);
      box(-0.7, 0.06, -0.3, -0.2, 0.68, 0.3, jitter(plastic));
      if (rand() < 0.7) {
        box(-0.28, 0.76, -0.12, 0.28, 1.12, -0.06, dark);   // a screen
        // Behind the panel rather than through it: overlapping it left both
        // resting on the desk at the same height, which is two faces on one
        // plane facing the same way.
        box(-0.08, 0.76, -0.05, 0.08, 0.8, 0.08, metal);
      }
    },
    workbench: (w) => {
      const half = Math.min(1.3, w / 2);
      box(-half, 0.86, 0.06, half, 0.94, 0.72, jitter(wood));
      for (const r of [-half + 0.1, half - 0.1]) box(r - 0.05, 0, 0.1, r + 0.05, 0.86, 0.68, metal);
      box(-half, 1.5, 0.06, half, 1.56, 0.3, jitter(metal));
      // Slotted rather than scattered. Two tools dropped at random landed on
      // top of each other often enough to matter, and both were sitting on the
      // bench at the same height.
      for (let i = 0; i < 3; i++) {
        const step = (2 * half) / 3;
        const r = -half + (i + 0.5) * step;
        const wide = Math.min(0.09, step * 0.4);
        box(r - wide, 0.94, 0.2, r + wide, 0.94 + 0.1 + rand() * 0.2, 0.44, jitter(i % 2 ? paint : metal));
      }
    },
    cabinet: () => {
      box(-0.45, 0, 0.06, 0.45, 1.4, 0.5, jitter(plastic));
      for (let i = 0; i < 3; i++) box(-0.4, 0.16 + i * 0.42, 0.5, 0.4, 0.2 + i * 0.42, 0.52, dark);
    },
    table: () => {
      box(-0.7, 0.72, -0.45, 0.7, 0.78, 0.45, jitter(wood));
      for (const r of [-0.6, 0.6]) for (const o of [-0.36, 0.36]) post(r, o, 0.035, 0, 0.72, metal, 5);
      for (const o of [-0.75, 0.75]) {
        box(-0.4, 0.42, o - 0.16, 0.4, 0.48, o + 0.16, jitter(wood));
        for (const r of [-0.32, 0.32]) post(r, o, 0.03, 0, 0.42, metal, 5);
      }
    },
    servery: (w) => {
      const half = Math.min(1.6, w / 2);
      box(-half, 0, 0.06, half, 1.02, 0.76, jitter(metal));
      box(-half - 0.06, 1.02, 0.0, half + 0.06, 1.1, 0.86, jitter(metal).multiplyScalar(1.12));
      for (let i = 0; i < 3; i++)
        box(-half + 0.2 + i * 0.5, 1.1, 0.2, -half + 0.6 + i * 0.5, 1.16, 0.62, jitter(paint));
    },
    noticeboard: () => {
      box(-0.55, 1.05, 0.06, 0.55, 1.85, 0.1, jitter(wood));
      // On a grid, jittered inside its own cell. Pinned at random they
      // overlapped, and two notes on one plane facing one way is a flicker
      // rather than a noticeboard.
      const paper = base.clone().lerp(new THREE.Color(0xffffff), 0.6);
      for (let row = 0; row < 2; row++) {
        for (let i = 0; i < 3; i++) {
          if (rand() < 0.18) continue;
          const r = -0.46 + i * 0.32 + rand() * 0.04;
          const u = 1.16 + row * 0.32 + rand() * 0.05;
          box(r, u, 0.1, r + 0.2, u + 0.22, 0.11, jitter(paper));
        }
      }
    },
    stall: (w) => {
      const half = span(w);
      for (const r of [-half, half]) post(r, 0.9, 0.05, 0, 2.3, jitter(wood), 5);
      // The awning, sloping out and down.
      for (let i = 0; i < 5; i++) {
        const o0 = 0.1 + i * 0.34;
        box(-half - 0.1, 2.3 - i * 0.1, o0, half + 0.1, 2.36 - i * 0.1, o0 + 0.34, jitter(i % 2 ? grey : dark));
      }
      box(-half, 0.78, 0.2, half, 0.86, 1.2, jitter(wood));
      // As many as the counter has room for, spread across it. Spaced by a
      // fraction of a fixed width, a narrow stall gave every item the same
      // place on the counter — four boxes on one plane, all facing up.
      const goods = Math.max(1, Math.min(4, Math.floor((2 * half) / 0.34)));
      const wide = Math.min(0.13, half / goods - 0.02);
      for (let i = 0; i < goods; i++) {
        const r = -half + ((i + 0.5) / goods) * 2 * half;
        box(r - wide, 0.86, 0.35, r + wide, 1.02 + rand() * 0.12, 0.95, jitter(i % 2 ? graphite : wood));
      }
    },
    planter: () => {
      box(-0.5, 0, -0.3, 0.5, 0.46, 0.3, jitter(base.clone().multiplyScalar(0.8)));
      // Nothing in the world is green any more; a planter is a pale mass.
      const green = grey.clone().multiplyScalar(0.92);
      for (let i = 0; i < 4; i++) {
        const r = -0.32 + rand() * 0.64;
        const o = -0.18 + rand() * 0.36;
        post(r, o, 0.13 + rand() * 0.08, 0.46, 0.7 + rand() * 0.5, jitter(green), 5);
      }
    },
    bollard: () => {
      post(0, 0.25, 0.11, 0, 0.95, jitter(metal), 7);
      post(0, 0.25, 0.13, 0.72, 0.8, metal, 7);
    },
    barrier: () => {
      box(-0.9, 0, 0.2, 0.9, 0.16, 0.74, jitter(paint));
      box(-0.72, 0.16, 0.3, 0.72, 0.82, 0.64, jitter(paint));
      box(-0.74, 0.6, 0.28, 0.74, 0.72, 0.66, dark);
    },
    /*
     * A chair, and the reason there was not one before.
     *
     * Four legs and a back is the most interpenetrating object in the kit —
     * every leg meets the seat and the back meets the seat again — and the
     * geometry tests reject any two boxes sharing a plane *and* a facing where
     * they overlap. So the legs stop under the seat rather than inside it, and
     * the back starts above it.
     */
    chair: () => {
      const t = 0.44;
      box(-0.22, t, -0.2, 0.22, t + 0.05, 0.2, jitter(wood));
      for (const r of [-0.18, 0.18]) for (const o of [-0.16, 0.16]) {
        box(r - 0.025, 0, o - 0.025, r + 0.025, t, o + 0.025, metal);
      }
      box(-0.2, t + 0.05, -0.2, 0.2, t + 0.52, -0.15, jitter(wood));
    },
    /* A few crushed cans, on their sides, going nowhere. */
    cans: () => {
      const n = 2 + Math.floor(rand() * 3);
      for (let i = 0; i < n; i++) {
        const r = -0.2 + rand() * 0.4;
        const o = 0.06 + rand() * 0.3;
        post(r, o, 0.033, 0, 0.11, i % 2 ? jitter(paint) : jitter(metal), 7);
      }
    },
    /* A carton somebody flattened and left. */
    carton: () => {
      const w2 = 0.16 + rand() * 0.12;
      box(-w2, 0, 0.05, w2, 0.03 + rand() * 0.14, 0.05 + w2 * 1.5, jitter(wood.clone().lerp(base, 0.3)));
    },
    /* A cone, because a street that has been dug up looks dug up. */
    cone: () => {
      box(-0.19, 0, -0.19, 0.19, 0.035, 0.19, jitter(plastic));
      post(0, 0, 0.1, 0.035, 0.52, jitter(rust), 6);
    },
    bin: () => {
      post(0, 0.32, 0.24, 0, 0.92, jitter(plastic), 8);
      post(0, 0.32, 0.26, 0.92, 0.98, dark, 8);
    },
    trolley: () => {
      box(-0.42, 0.24, 0.1, 0.42, 0.32, 0.86, jitter(metal));
      box(-0.42, 0.32, 0.78, 0.42, 1.05, 0.86, jitter(metal));
      for (const r of [-0.34, 0.34]) for (const o of [0.2, 0.76]) post(r, o, 0.07, 0.02, 0.24, dark, 6);
    },
    fire: () => {
      box(-0.22, 0.7, 0.06, 0.22, 1.5, 0.28, graphite);
      box(-0.18, 0.78, 0.28, 0.18, 1.42, 0.3, dark);
    },
    lamp: () => {
      box(-0.34, 2.5, 0.06, 0.34, 2.62, 0.3, jitter(metal));
      box(-0.3, 2.44, 0.1, 0.3, 2.5, 0.28, new THREE.Color(0xfff0d0));
    },
    vending: () => {
      box(-0.42, 0, 0.06, 0.42, 1.9, 0.78, jitter(plastic));
      box(-0.34, 0.55, 0.78, 0.28, 1.72, 0.8, new THREE.Color(0x1a2028));
      box(0.3, 0.4, 0.78, 0.4, 1.6, 0.8, grey);
      for (let row = 0; row < 4; row++)
        for (let i = 0; i < 4; i++)
          box(-0.3 + i * 0.15, 0.62 + row * 0.27, 0.8, -0.22 + i * 0.15, 0.8 + row * 0.27, 0.81, jitter(grey));
    },
    sign: () => { /* signs are their own pass, with a texture */ },

    // ---- facade fittings ---------------------------------------------------
    // What a building has on the outside of it. These are most of what turns a
    // lane between two blank slabs into a street, and they are placed against
    // exterior runs rather than by room, because a street does not have a use.
    downpipe: () => {
      const h = facadeHeight;
      post(0, 0.12, 0.075, 0, h, jitter(metal), 6);
      for (let u = 1.2; u < h; u += 2.4) box(-0.13, u, 0.02, 0.13, u + 0.09, 0.22, dark);
      box(-0.11, 0, 0.02, 0.11, 0.42, 0.28, jitter(metal));
    },
    aircon: () => {
      const u = 2.4 + rand() * Math.max(0.2, facadeHeight - 4.2);
      box(-0.42, u, 0.04, 0.42, u + 0.56, 0.5, jitter(metal));
      box(-0.34, u + 0.07, 0.5, 0.34, u + 0.49, 0.53, dark);
      box(-0.44, u - 0.1, 0.02, -0.3, u, 0.18, dark);
      box(0.3, u - 0.1, 0.02, 0.44, u, 0.18, dark);
    },
    awning: (w) => {
      const half = Math.min(1.5, Math.max(0.7, w / 2));
      const u = 2.5;
      for (let i = 0; i < 4; i++)
        box(-half, u - i * 0.11, 0.06 + i * 0.26, half, u - i * 0.11 + 0.07, 0.32 + i * 0.26,
          jitter(i % 2 ? grey : grey.clone().lerp(dark, 0.35)));
      for (const r of [-half + 0.05, half - 0.05]) box(r - 0.03, u - 0.4, 0.06, r + 0.03, u, 0.12, dark);
    },
    shutter: () => {
      box(-0.62, 0, 0.02, 0.62, 2.2, 0.1, jitter(metal).multiplyScalar(0.9));
      for (let u = 0.1; u < 2.2; u += 0.22) box(-0.6, u, 0.1, 0.6, u + 0.16, 0.13, jitter(metal));
      box(-0.68, 2.2, 0.0, 0.68, 2.46, 0.24, dark);
    },
    meter: () => {
      box(-0.24, 0.9, 0.04, 0.24, 1.55, 0.22, jitter(plastic));
      box(-0.2, 1.15, 0.22, 0.2, 1.34, 0.24, dark);
      post(0, 0.1, 0.045, 0, 0.9, metal, 5);
    },
    dish: () => {
      const u = 3.2 + rand() * Math.max(0.3, facadeHeight - 5);
      post(0, 0.2, 0.05, u, u + 0.5, metal, 5);
      box(-0.36, u + 0.3, 0.2, 0.36, u + 0.98, 0.3, jitter(base.clone().lerp(new THREE.Color(0xffffff), 0.4)));
      box(-0.05, u + 0.6, 0.3, 0.05, u + 0.7, 0.55, dark);
    },
    grille: () => {
      box(-0.36, 1.9, 0.02, 0.36, 2.4, 0.09, jitter(metal));
      for (let i = 0; i < 4; i++) box(-0.32, 1.96 + i * 0.12, 0.09, 0.32, 2.02 + i * 0.12, 0.11, dark);
    },
    cables: (w) => {
      const half = span(w);
      const u = 3.0 + rand() * Math.max(0.2, facadeHeight - 4.5);
      for (let i = 0; i < 3; i++)
        box(-half, u + i * 0.14, 0.08, half, u + i * 0.14 + 0.045, 0.125, dark);
      box(-0.1, u - 0.2, 0.04, 0.1, u + 0.5, 0.2, jitter(metal));
    },
    standpipe: () => {
      post(0, 0.14, 0.06, 0, 1.5, jitter(rust), 6);
      box(-0.2, 1.5, 0.03, 0.2, 1.68, 0.26, jitter(metal));
      box(-0.05, 1.1, 0.14, 0.05, 1.2, 0.4, dark);
    },
    washing: (w) => {
      const half = span(w);
      const u = 2.6;
      box(-half, u, 0.1, half, u + 0.03, 0.13, dark);
      for (let i = 0; i < 4; i++) {
        const r = -half + 0.25 + (i / 3) * (2 * half - 0.5);
        box(r - 0.16, u - 0.5 - rand() * 0.3, 0.09, r + 0.16, u, 0.11, jitter(grey));
      }
    },
  };

  // ---- sockets -------------------------------------------------------------

  /** Every opening on a run, as intervals along it, with a margin. */
  const gapsOf = (openings: { t0: number; t1: number }[]): [number, number][] =>
    openings.map((o) => [o.t0 - 0.7, o.t1 + 0.7] as [number, number]);

  const wallSockets: Socket[] = [];
  const cornerSockets: Socket[] = [];
  for (const run of level.boundaries) {
    const gaps = gapsOf(run.openings);
    const len = run.emitEnd - run.emitStart;
    if (len < 1.0) continue;
    for (let side = 0; side < 2; side++) {
      if (run.sides[side] === OUTSIDE) continue;
      const n = side === 0 ? -1 : 1;
      const nx = run.axis === 'x' ? n : 0;
      const ny = run.axis === 'y' ? n : 0;
      const face = run.coord + n * (wt / 2);
      // One socket every couple of metres, and one at each end for the corners.
      const step = 1.8;
      const count = Math.max(1, Math.floor(len / step));
      for (let i = 0; i < count; i++) {
        const t = run.emitStart + ((i + 0.5) / count) * len;
        if (gaps.some(([a, b]) => t > a && t < b)) continue;
        const x = run.axis === 'x' ? face : t;
        const y = run.axis === 'x' ? t : face;
        // The room this stretch of wall is actually standing in: a run can be
        // longer than the part of it that faces any one place.
        const cx = Math.floor((x + nx * 0.5) / g);
        const cy = Math.floor((y + ny * 0.5) / g);
        const place = placeAtCell(run.layer, cx, cy);
        if (!place) continue;
        const s: Socket = { x, y, z: place.floor, nx, ny, room: Math.min(step, len / count), place };
        if (i === 0 || i === count - 1) cornerSockets.push(s);
        wallSockets.push(s);
      }
    }
  }

  /** Cells with floor all round them: somewhere to stand something. */
  const openSockets: Socket[] = [];
  for (const place of places) {
    for (const k of place.cells) {
      const c = k.indexOf(',');
      const x = Number(k.slice(0, c));
      const y = Number(k.slice(c + 1));
      let ring = 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (place.cells.has(`${x + dx},${y + dy}`)) ring++;
      if (ring < 4) continue;
      openSockets.push({
        x: (x + 0.5) * g, y: (y + 0.5) * g, z: place.floor,
        nx: 0, ny: 1, room: g, place,
      });
    }
  }

  /**
   * Anything the compiler already put there is not free space.
   *
   * Only what stands in the way at standing height: a floor slab is under
   * everything and a roof is over it, and treating either as an obstacle
   * empties the room.
   */
  const taken: { x0: number; y0: number; x1: number; y1: number; z0: number; z1: number }[] = [];
  for (const s of level.solids) {
    if (s.role === 'floor' || s.role === 'roof') continue;
    taken.push({
      x0: s.box.min[0] - 0.1, y0: s.box.min[1] - 0.1,
      x1: s.box.max[0] + 0.1, y1: s.box.max[1] + 0.1,
      z0: s.box.min[2], z1: s.box.max[2],
    });
  }
  const clashes = (s: Socket, reach: number): boolean =>
    taken.some(
      (t) =>
        s.x + reach > t.x0 && s.x - reach < t.x1 &&
        s.y + reach > t.y0 && s.y - reach < t.y1 &&
        t.z1 > s.z + 0.15 && t.z0 < s.z + 1.6,
    );

  /*
   * Everything already standing, as world boxes.
   *
   * A fixture may overlap itself as much as it likes — a clamp goes round a
   * pipe, a painted band round a barrier — but two of them sharing a volume is
   * a solid growing out of another solid, and two of them sharing a plane is
   * z-fighting. Both are the defects `checkGeometry` rejects in a compiled
   * level, and the dressing is held to the same standard.
   */
  const standing: Extent[] = [];
  /** Fixtures have to clear each other by this much, so none of them touch. */
  const GAP = 0.02;

  /**
   * Draw a fixture, and take it back if it lands in something already there.
   *
   * Measured rather than predicted: the fixture is drawn, its true bounds are
   * read off the vertices it emitted, and if those bounds are in anything the
   * emission is rewound. The buffers are plain arrays and the indices are
   * absolute, so truncating them is an exact undo.
   */
  const stand = (draw: () => void): boolean => {
    const p0 = pos.length, n0 = nrm.length, c0 = col.length, i0 = idx.length;
    const t0 = tris;
    const tr0 = trace ? trace.length : 0;
    instance++;
    extent = { x0: Infinity, y0: Infinity, z0: Infinity, x1: -Infinity, y1: -Infinity, z1: -Infinity };
    draw();
    const box3 = extent;
    extent = null;
    // Nothing drawn: a recipe that rolled its way out of putting anything here.
    if (box3.x0 === Infinity) return true;
    const hit = standing.some(
      (s) =>
        Math.min(s.x1, box3.x1) - Math.max(s.x0, box3.x0) > -GAP &&
        Math.min(s.y1, box3.y1) - Math.max(s.y0, box3.y0) > -GAP &&
        Math.min(s.z1, box3.z1) - Math.max(s.z0, box3.z0) > -GAP,
    );
    if (hit) {
      pos.length = p0; nrm.length = n0; col.length = c0; idx.length = i0;
      tris = t0;
      if (trace) trace.length = tr0;
      return false;
    }
    standing.push(box3);
    return true;
  };

  // ---- placement -----------------------------------------------------------

  const shuffle = <T,>(list: T[]): T[] => {
    const out = list.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  };

  const pools: Record<Recipe['where'], Map<string, Socket[]>> = {
    wall: new Map(), open: new Map(), corner: new Map(),
  };
  const bucket = (where: Recipe['where'], list: Socket[]): void => {
    for (const s of shuffle(list)) {
      let arr = pools[where].get(s.place.id);
      if (!arr) pools[where].set(s.place.id, (arr = []));
      arr.push(s);
    }
  };
  bucket('wall', wallSockets);
  bucket('open', openSockets);
  bucket('corner', cornerSockets);

  /** Where something already stands, and how much elbow room it wanted. */
  const used: { x: number; y: number; z: number; r: number }[] = [];
  const tooClose = (s: Socket, r: number): boolean =>
    used.some((u) => Math.hypot(u.x - s.x, u.y - s.y) < (u.r + r) * 0.5 && Math.abs(u.z - s.z) < 1.5);

  /** Open-floor fixtures are the ones that can get in the way; keep them small. */
  const FOOTPRINT: Partial<Record<Fixture, number>> = {
    // A stall is its awning, which reaches beyond the counter in both
    // directions; measured by the counter it kept landing inside its neighbour.
    rack: 1.7, shelf: 2.0, stall: 3.0, tank: 1.8, duct: 2.8, servery: 1.8,
    desk: 1.9, table: 2.0, barrier: 2.2, trolley: 1.5, pallet: 1.6,
    // The ones that run along a wall need at least their own length of
    // clearance, or the next one starts inside this one.
    pipes: 2.8, tray: 2.8, cables: 2.8, washing: 2.4,
  };

  for (const place of places) {
    const plan = COMPOSE[place.use] ?? COMPOSE.hall;
    // The hero first, so it gets the pick of the room, then as many supports
    // as the floor is entitled to. One of each, whatever the area.
    const recipes = [plan.hero, ...plan.supports.slice(0, supportCount(place.area))];
    for (const recipe of recipes) {
      if (tris > budget) break;
      const pool = pools[recipe.where].get(place.id);
      if (!pool?.length) continue;
      const reach = FOOTPRINT[recipe.fixture] ?? 1.0;
      for (let n = 0; n < 1 && pool.length; n++) {
        for (let attempt = 0; attempt < 8 && pool.length; attempt++) {
          const s = pool.pop()!;
          if (tooClose(s, reach)) continue;
          if (recipe.where === 'open' && clashes(s, reach * 0.5)) continue;
          ox = s.x; oy = s.y; oz = s.z;
          fx = s.nx; fy = s.ny;
          drawing = recipe.fixture;
          if (recipe.where === 'open') {
            // Nothing to face, so face somewhere and be done with it.
            const a = Math.floor(rand() * 4) * (Math.PI / 2);
            fx = Math.round(Math.sin(a));
            fy = Math.round(Math.cos(a));
          }
          if (!stand(() => FIXTURES[recipe.fixture](s.room))) continue;
          used.push({ x: s.x, y: s.y, z: s.z, r: reach });
          break;
        }
      }
    }
  }

  /*
   * What a place collects, as opposed to what it is furnished with.
   *
   * The composition above is deliberate about scarcity — one hero, a few
   * supports, once each, because negative space is the thing being composed.
   * That is right for furniture and wrong for litter, and the two are not the
   * same argument. A street with nothing on it does not read as composed, it
   * reads as unbuilt: no cans in the gutter, no cone by the hole, no chair
   * pulled out from a table and left there.
   *
   * So litter is its own pass, after the furniture and before the services.
   * Small, low, close to the walls where things actually accumulate, and never
   * anywhere the furniture already is — `stand` rewinds anything that lands in
   * something, so the two passes cannot fight.
   */
  const LITTER: Record<string, Fixture[]> = {
    yard: ['cans', 'carton', 'cone', 'bottles', 'crates'],
    route: ['cans', 'carton', 'cone'],
    market: ['cans', 'carton', 'bottles', 'chair'],
    concourse: ['cans', 'carton', 'bin'],
    hall: ['cans', 'chair'],
    canteen: ['chair', 'chair', 'cans', 'bottles'],
    office: ['chair', 'carton'],
    workshop: ['carton', 'cans', 'tyres', 'pallet'],
    store: ['carton', 'pallet', 'crates'],
    plant: ['carton', 'cans', 'barrels'],
    dock: ['pallet', 'crates', 'carton', 'cone'],
    stair: ['cans'],
    terrace: ['cans', 'chair', 'carton'],
    ward: ['chair', 'carton'],
    lab: ['carton'],
    vault: ['carton'],
  };
  for (const place of places) {
    if (tris > budget) break;
    const kit = LITTER[place.use] ?? LITTER.hall;
    if (!kit?.length) continue;
    // Roughly one piece per forty square metres, and never more than six: this
    // is meant to read as neglect, not as a jumble sale.
    const want = Math.min(6, Math.round(place.area / 40));
    const pool = shuffle([...(pools.wall.get(place.id) ?? []), ...(pools.open.get(place.id) ?? [])]);
    let put = 0;
    for (const socket of pool) {
      if (put >= want || tris > budget) break;
      const fixture = kit[Math.floor(rand() * kit.length)];
      if (tooClose(socket, 0.7)) continue;
      ox = socket.x; oy = socket.y; oz = socket.z;
      fx = socket.nx; fy = socket.ny;
      if (!socket.nx && !socket.ny) {
        const a = Math.floor(rand() * 4) * (Math.PI / 2);
        fx = Math.round(Math.sin(a));
        fy = Math.round(Math.cos(a));
      }
      drawing = fixture;
      if (!stand(() => FIXTURES[fixture](socket.room))) continue;
      used.push({ x: socket.x, y: socket.y, z: socket.z, r: 0.7 });
      put++;
    }
  }

  /*
   * The dark side of the room.
   *
   * A world made of one pale material has one failure mode above all others:
   * everything is the same value, so nothing is foreground and nothing is
   * background and the frame is a sheet of paper. It needs mass — something
   * genuinely dark, at floor level, in most views — and the honest place for
   * mass in an architectural simulation is where the services run.
   *
   * So each room of any size gets a recessed graphite channel along one wall,
   * with a few machined blocks standing on it. It is one continuous dark band
   * rather than fifteen scattered dark objects, which is the difference
   * between a composition and a scatter, and it is set into the floor so it
   * never becomes something to trip over.
   */
  for (const place of [...places].filter((p) => p.area > 38).sort((a, b) => b.area - a.area).slice(0, 5)) {
    const w = place.max[0] - place.min[0];
    const d = place.max[1] - place.min[1];
    const alongX = w >= d;
    const half = Math.min(alongX ? w : d, 22) / 2 - 0.4;
    if (half < 1.2) continue;
    ox = alongX ? (place.min[0] + place.max[0]) / 2 : place.min[0];
    oy = alongX ? place.min[1] : (place.min[1] + place.max[1]) / 2;
    oz = place.floor;
    fx = alongX ? 0 : 1;
    fy = alongX ? 1 : 0;
    drawing = 'services';
    stand(() => {
      // The channel: a dark inset, its lip flush with the floor.
      box(-half, 0.0, 0.08, half, 0.055, 0.62, dark);
      // And what runs in it. Machined blocks at intervals, not a scatter:
      // regular spacing is what says "this was installed" rather than "this
      // was dropped".
      const blocks = Math.max(2, Math.min(5, Math.floor(half / 2.4)));
      for (let i = 0; i < blocks; i++) {
        const r = -half + ((i + 0.5) / blocks) * half * 2;
        const tall = 0.5 + ((i * 37) % 5) * 0.13;
        box(r - 0.34, 0.055, 0.14, r + 0.34, 0.055 + tall, 0.56, graphite);
        box(r - 0.24, 0.055 + tall, 0.2, r + 0.24, 0.055 + tall + 0.07, 0.5, dark);
      }
    });
  }

  /*
   * One wall in the room is a colour.
   *
   * This is the whole of the map's colour budget and it is spent in one place
   * on purpose. A saturated plane has force against white architecture only
   * because there is almost none of it; the same pigment spread over crates,
   * pipes, benches and signs is not five times the colour, it is no colour at
   * all, because nothing is picked out by it any more.
   *
   * So the largest rooms on the floor get one accent plane each, on the long
   * wall at the far end — where it is something you walk toward and therefore
   * something you navigate by. Drawn through the same emitter as every other
   * fixture, so it is one more box in one more merged mesh: it moves nothing,
   * blocks nothing, and costs no draw call of its own.
   */
  const hero = [...places].filter((p) => p.area > 55).sort((a, b) => b.area - a.area)[0];
  if (hero) {
    /*
     * A thing, not a coat of paint.
     *
     * The first version of this put a six-and-a-half metre panel of saturated
     * colour on the far wall of each of the two largest rooms. Measured from
     * the fixed viewpoints it passed every gate; walked up to, it is a blue
     * wall — twenty square metres of pigment filling a sixth of the frame from
     * inside the room it is supposed to be a landmark at the end of. And two
     * of them in two rooms is not one intervention, it is two, so neither is
     * *the* colour of the map.
     *
     * So there is exactly one, in the largest room, and it is a gate: two
     * jambs and a head, standing free, across the room's long axis. Four times
     * less pigment for the same landmark, because the members are narrow — but
     * more importantly it is a piece of architecture rather than a decoration.
     * You walk through it, which means it marks a route rather than a surface,
     * and it still reads from the other end of the map because it is tall.
     */
    const w = hero.max[0] - hero.min[0];
    const d = hero.max[1] - hero.min[1];
    const alongX = w >= d;
    const across = Math.min(alongX ? d : w, 6.8);
    const clear = Math.min(3.4, Math.max(2.2, (hero.ceiling - hero.floor) * 0.56));
    const half = Math.max(1.3, across * 0.36);
    // Slim. From a metre away even a gate is a wall if its members are thick,
    // and the sweep found three maps whose worst view was a sixth accent.
    const jamb = 0.17;

    // Two-thirds of the way down the room, so it is something ahead of you
    // when you come in rather than something you are already standing at.
    ox = alongX ? hero.min[0] + w * 0.68 : (hero.min[0] + hero.max[0]) / 2;
    oy = alongX ? (hero.min[1] + hero.max[1]) / 2 : hero.min[1] + d * 0.68;
    oz = hero.floor;
    fx = alongX ? 1 : 0;
    fy = alongX ? 0 : 1;
    drawing = 'accent';
    stand(() => {
      for (const side of [-1, 1]) {
        box(side * half - jamb / 2, 0, -0.11, side * half + jamb / 2, clear, 0.11, accent);
      }
      box(-half - jamb / 2, clear, -0.11, half + jamb / 2, clear + jamb, 0.11, accent);
    });
  }

  /*
   * One piece of architecture the room is about.
   *
   * Everything else this file emits is a fixture: a crate, a rack, a bollard,
   * a sign. Fixtures tell you what a room is *used* for and nothing at all
   * about what it *is*, and a hundred and five maps dressed entirely in
   * fixtures come out as a hundred and five differently-shaped rooms with the
   * same furniture in them.
   *
   * A hero is the other thing. It is map-scale, it is chosen by the kind of
   * building this is pretending to be rather than by what the room's use table
   * happened to say, and there is exactly one on the floor — in the largest
   * space, where the fight is. A vending machine cannot be the subject of a
   * six-hundred-square-metre hall; a bridge across it can.
   *
   * All six live above head height or lie flat in the floor. That is not
   * squeamishness, it is honesty: nothing this file emits collides, so a
   * monumental stair or a sunken court would be a staircase you walk through
   * and a hole you stand on. Anything you could walk into has to come from the
   * compiler, where the navmesh can see it. What is left is still plenty —
   * light from above, a route crossing overhead, a mass hanging in the volume,
   * a floor plane doing something — and all of it changes the section of the
   * room, which is the thing fixtures cannot touch.
   */
  if (hero) {
    const hw = hero.max[0] - hero.min[0];
    const hd = hero.max[1] - hero.min[1];
    const alongLong = hw >= hd;
    const height = Math.max(3.0, hero.ceiling - hero.floor);
    /** Half the room, along and across the long axis. */
    const along = (alongLong ? hw : hd) / 2;
    const across = (alongLong ? hd : hw) / 2;
    ox = (hero.min[0] + hero.max[0]) / 2;
    oy = (hero.min[1] + hero.max[1]) / 2;
    oz = hero.floor;
    fx = alongLong ? 1 : 0;
    fy = alongLong ? 0 : 1;
    drawing = 'hero';

    switch (HERO[languageOf(seed)]) {
      /*
       * A hole in the roof, with depth to it.
       *
       * The cheapest way to make a room feel like it is inside a building
       * rather than inside a box: light arrives from a place with a shape.
       * The reveals are what sell it — a rectangle of brightness on a flat
       * ceiling is a light fitting, and a rectangle set back behind a foot of
       * structure is an opening.
       */
      case 'lightwell': {
        const a = Math.min(along * 0.5, 5.5);
        const b = Math.min(across * 0.55, 4);
        const lip = 0.34;
        const deep = Math.min(1.5, height * 0.28);
        stand(() => {
          // Four reveals standing down from the ceiling, framing the opening.
          for (const side of [-1, 1]) {
            box(-a - lip, height - deep, side * b, a + lip, height, side * (b + lip), base);
            // Meeting the pair above at their edge rather than crossing them:
            // two boxes sharing a corner share a plane, and two coplanar faces
            // pointing the same way are the flicker this whole emitter's
            // geometry checks exist to catch.
            box(side * (a + lip), height - deep, -b, side * a, height, b, base);
          }
          // And a bright soffit inside it, catching whatever is up there.
          box(-a, height + deep * 0.55, -b, a, height + deep * 0.6, b, base);
        });
        break;
      }

      /*
       * A route crossing over the fight.
       *
       * The single most useful thing a room can have: it says the building
       * continues above you, it draws a hard horizontal across a vertical
       * space, and the shadow it throws is worth more than anything else in
       * this file. Run past the room's own walls at both ends so it reads as
       * passing through rather than stopping in mid air.
       */
      case 'bridge': {
        const deck = Math.min(height - 2.1, 5.4);
        if (deck < 3.2) break;
        const wide = Math.min(across * 0.42, 1.7);
        const reach = along + 1.2;
        stand(() => {
          box(-reach, deck, -wide, reach, deck + 0.22, wide, graphite);
          box(-reach, deck + 0.22, -wide, reach, deck + 0.3, wide, grey);
          // A balustrade, because an unrailed deck reads as a shelf.
          for (const side of [-1, 1]) {
            box(-reach, deck + 0.94, side * wide - 0.07, reach, deck + 1.04, side * wide + 0.07, graphite);
            for (let i = -3; i <= 3; i++) {
              const at = (i / 3) * reach * 0.86;
              box(at - 0.06, deck + 0.3, side * wide - 0.05, at + 0.06, deck + 0.94, side * wide + 0.05, graphite);
            }
          }
        });
        break;
      }

      /*
       * A wall that is a system rather than a surface.
       *
       * Vertical fins on the long wall, evenly spaced, standing a hand's
       * breadth proud. What they actually do is convert one flat lit plane
       * into forty alternating lit and shaded ones, which is the difference
       * between a white wall and a white wall you can tell the depth of.
       */
      case 'louver_wall': {
        const top = Math.min(height - 0.4, 5.6);
        const step = 0.9;
        const count = Math.floor((along * 1.9) / step);
        if (count < 4) break;
        stand(() => {
          box(-along, 0.9, across - 0.02, along, 1.16, across + 0.14, graphite);
          for (let i = 0; i < count; i++) {
            const at = -along + 0.4 + i * step;
            if (at > along - 0.4) break;
            box(at - 0.11, 1.16, across + 0.01, at + 0.11, top, across + 0.2, grey);
          }
          box(-along, top, across - 0.02, along, top + 0.24, across + 0.14, graphite);
        });
        break;
      }

      /*
       * A mass in the volume, hung from the structure.
       *
       * Industrial buildings are full of these — plant, ducting, a hopper —
       * and what they do for a room is give the empty air above the fight a
       * scale. Hangers are thick enough to read at distance; a box held up by
       * wires is a box floating.
       */
      case 'hanging_volume': {
        const bottom = Math.min(height - 1.8, 3.6);
        if (bottom < 2.9) break;
        const a = Math.min(along * 0.34, 2.6);
        const b = Math.min(across * 0.4, 1.5);
        const tall = Math.min(height - bottom - 0.5, 1.9);
        stand(() => {
          box(-a, bottom, -b, a, bottom + tall, b, graphite);
          box(-a * 0.82, bottom + tall, -b * 0.82, a * 0.82, bottom + tall + 0.2, b * 0.82, dark);
          for (const rs of [-1, 1]) for (const os of [-1, 1]) {
            box(rs * a * 0.74 - 0.09, bottom + tall + 0.2, os * b * 0.7 - 0.09, rs * a * 0.74 + 0.09, height, os * b * 0.7 + 0.09, graphite);
          }
        });
        break;
      }

      /*
       * An upper floor pushed out over the room.
       *
       * A projecting slab with visible brackets under it. The brackets are the
       * whole point: the brief's rule against floating platforms is really a
       * rule about load having somewhere to go, and two struts back to the
       * wall is the cheapest possible way of saying where.
       */
      case 'cantilever': {
        const under = Math.min(height - 2.0, 4.2);
        if (under < 3.0) break;
        const a = Math.min(along * 0.55, 5);
        const out = Math.min(across * 0.62, 3.2);
        stand(() => {
          box(-a, under, across - out, a, under + 0.34, across, graphite);
          box(-a, under + 0.34, across - out, a, under + 0.5, across, base);
          box(-a, under + 0.5, across - out, a, under + 1.5, across - out + 0.16, base);
          for (const side of [-0.72, 0, 0.72]) {
            const at = side * a;
            box(at - 0.14, under - 0.9, across - out * 0.45, at + 0.14, under, across - 0.1, graphite);
          }
        });
        break;
      }

      /*
       * Water, or something standing in for it.
       *
       * Flat, so it can be walked over without lying about collision, and very
       * dark against the polished floor it sits in. It reads because the room
       * above it is pale: a black rectangle in the middle of a white hall is
       * the one composition in this list that needs no height at all.
       */
      case 'reflecting_pool': {
        const a = Math.min(along * 0.55, 6);
        const b = Math.min(across * 0.5, 4);
        stand(() => {
          box(-a, 0.0, -b, a, 0.035, b, dark);
          // A kerb around it, in four pieces that meet at the corners rather
          // than a slab the water is buried in.
          for (const side of [-1, 1]) {
            box(-a - 0.3, 0.0, side * b, a + 0.3, 0.13, side * (b + 0.3), base);
            box(side * (a + 0.3), 0.0, -b, side * a, 0.13, b, base);
          }
        });
        break;
      }
    }
    drawing = '';
  }

  /*
   * The outside of the buildings.
   *
   * Most of this library is traced from radars, and a radar trace is three or
   * four open areas with buildings all round them — so most of what a player
   * looks at is an exterior wall, and an exterior wall with nothing on it is
   * the single biggest reason a street reads as a canyon. None of this belongs
   * to a room: a downpipe is a property of the building, not of the yard it
   * happens to stand in, so it is placed against the run rather than by use.
   */
  /*
   * Facade fittings, minus the ones made of wire.
   *
   * `cables` was three bars four and a half centimetres thick strung two and a
   * half metres across a wall, and `washing` was four hanging cloths on a
   * three-centimetre line. Seen anywhere near edge-on — which on a facade is
   * most angles — geometry that thin is a row of sub-pixel slivers, and what
   * it produces is a cluster of flickering black specks that reads, correctly,
   * as rendering corruption. It was the artefact on the upper walls.
   *
   * Minimalism does not hide bad geometry, it exhibits it: with nothing else
   * on the wall, the one thing that shimmers is the thing you look at. So the
   * wire-thin fittings are gone rather than nudged, and what stays is the kit
   * with real depth to it.
   */
  const FACADE_KIT: [Fixture, number][] = [
    ['downpipe', 1.0], ['aircon', 0.9], ['grille', 0.8], ['meter', 0.6],
    ['standpipe', 0.5], ['awning', 0.5], ['shutter', 0.5],
  ];
  const kitTotal = FACADE_KIT.reduce((a, [, w]) => a + w, 0);
  /** Where the top of each boundary run's masonry is, so a pipe reaches it. */
  const runTop = new Map<string, number>();
  for (const s of level.solids) {
    if (!s.boundary) continue;
    runTop.set(s.boundary, Math.max(runTop.get(s.boundary) ?? 0, s.box.max[2]));
  }

  /** Ground already covered by a coping, as (x0, x1, y0, y1) in canonical metres. */
  const capped_: [number, number, number, number][] = [];
  for (const run of level.boundaries) {
    if (!run.exterior) continue;
    const len = run.emitEnd - run.emitStart;
    if (len < 2.2) continue;
    const side = run.sides[0] === OUTSIDE ? 1 : 0;
    if (run.sides[side] === OUTSIDE) continue;
    const n = side === 0 ? -1 : 1;
    const nx = run.axis === 'x' ? n : 0;
    const ny = run.axis === 'y' ? n : 0;
    const face = run.coord + n * (wt / 2);
    const gaps = gapsOf(run.openings);
    const top = runTop.get(run.id) ?? 6;

    /*
     * One fitting every couple of metres, jittered, never over a door — and
     * never inside the last one. The jitter is what makes a wall look unplanned
     * and is also what put two of them in the same place, which is two surfaces
     * on one plane rather than a busier wall.
     */
    /*
     * One fitting every nine metres, and most runs get none at all.
     *
     * At one every two point two this put an air-conditioning unit, a grille,
     * a meter or a dish on every stretch of every exterior wall in the map —
     * five hundred of them on one floor, all small, all graphite, all at the
     * same importance. That is not detail, it is grain: it makes a facade
     * look busy without making any part of it worth looking at, and it spends
     * the whole of the dark-mass budget on things two centimetres across.
     *
     * Sparse enough that a fitting is an event on a wall rather than a texture
     * across it, and the run has to be long enough to deserve one.
     */
    /*
     * A cornice: the top of every outside wall, in graphite.
     *
     * The one piece of graphite an open map is guaranteed to have. Everything
     * else in the vocabulary that is dark lives on a ceiling, inside a
     * machine, or under a roof — and half this library is traced from radars,
     * which means three or four open areas with buildings all round them, no
     * interiors and nothing overhead. On those maps every frame came out a
     * hundred per cent pale: a picture with no bottom to its range, no weight,
     * and a coral hostile against a field of white with nothing in between.
     *
     * At the top rather than at the bottom, which is where it started. A base
     * course is the more obvious answer and it is the wrong one — the bottom
     * metre of a wall is where the downpipes land, where the service channels
     * run, where the pallets stack, and a continuous band through all of that
     * shares a plane with something on every second wall in the map. The
     * roofline has nothing else on it. It is also the better line: it is what
     * the eye follows in a street, it caps buildings that otherwise simply
     * stop, and it is in frame from anywhere outdoors rather than only when
     * you are looking down.
     */
    /*
     * Once per line of wall, however many runs are on it.
     *
     * Two buildings can back onto the same wall line, and a boundary can be
     * split into several runs along it, so the same stretch of roofline is
     * offered more than once. Drawn twice it is two coincident surfaces
     * flickering against each other along the top of a building, which is the
     * most conspicuous place in the map to have one.
     *
     * Keyed on the *wall*, not on the face, and the band wraps both sides of
     * it. Two buildings backing onto one wall are two runs with opposite
     * outward directions on the same line, and capping each of them
     * separately puts two bands through the same volume. One symmetric band
     * cannot do that, and it is what a coping actually is: a course laid over
     * the top of the wall, not a strip stuck to one face of it.
     *
     * Height is deliberately not part of the key either. Two boundaries on
     * one line can report tops a few centimetres apart, which is exactly the
     * case that produces a flicker rather than avoiding one.
     */
    /*
     * Near enough is the same wall.
     *
     * Measured as the ground the band would actually cover rather than as the
     * run's own coordinate, because the two are not the same question. A run
     * is a boundary between two named spaces; several of them can lie on one
     * wall, walls half a metre apart carry bands wide enough to overlap
     * completely, and a floorplan traced off a radar is full of both. What
     * matters is only whether this band would be laid over one already laid.
     */
    const wide = wt / 2 + 0.2;
    const band: [number, number, number, number] = run.axis === 'x'
      ? [run.coord - wide, run.coord + wide, run.emitStart, run.emitEnd]
      : [run.emitStart, run.emitEnd, run.coord - wide, run.coord + wide];
    const clash = capped_.some(
      (c) => Math.min(c[1], band[1]) - Math.max(c[0], band[0]) > 0.05
        && Math.min(c[3], band[3]) - Math.max(c[2], band[2]) > 0.05,
    );
    if (!clash) capped_.push(band);

    if (len >= 2.2 && top > 2.4 && !clash) {
      /*
       * Centred on the wall, and started at the end the frame runs from.
       *
       * The placement frame's `right` axis follows the outward direction: for
       * a run whose outside is on one side it increases along the wall, and
       * for one whose outside is on the other it decreases. Every other
       * fixture in this file is a point placement and never notices, because
       * it sets its own origin at the spot it stands on. A band the length of
       * the wall does notice — anchored at the wrong end it is drawn the
       * whole length of the run in the wrong direction, off the end of the
       * building and over whatever is beyond it.
       */
      const from = run.axis === 'x'
        ? (n < 0 ? run.emitStart : run.emitEnd)
        : (n > 0 ? run.emitStart : run.emitEnd);
      ox = run.axis === 'x' ? run.coord : from;
      oy = run.axis === 'x' ? from : run.coord;
      oz = top;
      fx = nx;
      fy = ny;
      drawing = 'cornice';
      /*
       * Not through `stand`: a cornice is meant to be continuous, and the
       * interpenetration guard would drop every second stretch of it for
       * touching the one before. Trimmed by a wall thickness at each end so
       * two runs meeting at a corner meet rather than overlap — the gap is
       * behind the corner and cannot be seen from anywhere in the map.
       */
      const deep = Math.min(0.95, Math.max(0.5, top * 0.11));
      // Back from both ends by more than the cornice projects, so that two
      // perpendicular runs meeting at a corner stop short of each other
      // rather than crossing. The notch faces outward at the corner and is
      // read as a joint rather than as a gap.
      // Clear of a perpendicular coping's own projection at the corner, with
      // room to spare: the two used to graze each other by a centimetre or
      // two, which is not a joint, it is a flicker.
      const trim = wt * 0.5 + 0.62;
      const end = Math.max(trim + 0.3, len - trim);
      const half = wt / 2;
      /*
       * One direction laps over the other.
       *
       * Copings running north-south sit a course above the ones running
       * east-west, so that where two of them cross — and on a dense
       * floorplan they cross constantly, at every wall that meets another
       * wall away from a corner — they step rather than coincide. Two
       * identical bands at identical heights sharing half a square metre of
       * horizontal face is a shimmer along the top of a building, which is
       * about the most conspicuous place in the map to put one.
       *
       * It is also simply what masonry does: at a junction one course has to
       * run through and the other has to stop against it.
       */
      const lap = run.axis === 'x' ? 0 : 0.07;
      box(trim, -deep - lap, -half - 0.1, end, -0.06 - lap, half + 0.1, graphite);
      box(trim, -0.06 - lap, -half - 0.16, end, 0.02 - lap, half + 0.16, grey);
      drawing = '';
    }

    if (len < 6) continue;
    const count = Math.max(1, Math.round(len / 9));
    let lastAt = -Infinity;
    let lastNeeded = 0;
    for (let i = 0; i < count; i++) {
      const t = run.emitStart + ((i + 0.15 + rand() * 0.7) / count) * len;
      if (gaps.some(([a, b]) => t > a && t < b)) continue;
      const x = run.axis === 'x' ? face : t;
      const y = run.axis === 'x' ? t : face;
      const cx = Math.floor((x + nx * 0.5) / g);
      const cy = Math.floor((y + ny * 0.5) / g);
      const place = placeAtCell(run.layer, cx, cy);
      if (!place) continue;
      facadeHeight = Math.max(2.6, top - place.floor);
      let roll = rand() * kitTotal;
      let pick: Fixture = 'downpipe';
      for (const [f, w] of FACADE_KIT) {
        roll -= w;
        if (roll <= 0) { pick = f; break; }
      }
      // An awning or a shutter belongs beside a door, not halfway up a blank
      // wall; if there is no opening on this run, pick something else.
      if ((pick === 'awning' || pick === 'shutter') && !run.openings.length) pick = 'aircon';
      const needs = FOOTPRINT[pick] ?? 1.1;
      // Clear of the last fitting on this run, and clear of anything a room
      // recipe has already put on the same wall — the facade pass used to
      // consult neither, so an air-conditioning unit could be bolted through
      // a cable tray.
      if (t - lastAt < (lastNeeded + needs) * 0.5) continue;
      const socket: Socket = { x, y, z: place.floor, nx, ny, room: needs, place };
      if (tooClose(socket, needs)) continue;
      ox = x; oy = y; oz = place.floor;
      fx = nx; fy = ny;
      drawing = pick;
      if (!stand(() => FIXTURES[pick](Math.min(2.6, len / count)))) continue;
      lastAt = t;
      lastNeeded = needs;
      used.push({ x, y, z: place.floor, r: needs });
      if (tris > budget) break;
    }
    if (tris > budget) break;
  }

  // ---- signs ---------------------------------------------------------------
  const signs = buildSigns(spec, level, places, seed, placeAtCell, wt);
  if (signs) group.add(signs.mesh);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.82,
    metalness: 0.08,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'props';
  group.add(mesh);

  /*
   * Where everything ended up, so the player can bump into it.
   *
   * These are the same boxes the placement pass measured to stop two fixtures
   * standing in each other — read off the vertices actually emitted rather
   * than predicted, which is what makes them worth colliding against. Until
   * now they were used once and thrown away, and a vending machine was a
   * picture of a vending machine.
   */
  return {
    group,
    solids: standing.map((b) => ({ x0: b.x0, y0: b.y0, z0: b.z0, x1: b.x1, y1: b.y1, z1: b.z1 })),
    triangles: tris + (signs?.triangles ?? 0),
    dispose() {
      geo.dispose();
      mat.dispose();
      signs?.dispose();
    },
  };
}

// -----------------------------------------------------------------------------
// Signage
//
// The single strongest thing you can put in a greybox. A room with its name on
// the wall is a room somebody named; two rooms with different names on the
// wall are two different places even if they are the same box. And the names
// are already there — the plans are traced from real maps and keep the areas'
// real names — so nothing has to be invented.

interface Signs {
  mesh: THREE.Mesh;
  triangles: number;
  dispose(): void;
}

function buildSigns(
  spec: LevelSpec,
  level: CompiledLevel,
  places: Place[],
  seed: string,
  placeAt: (layer: string, cx: number, cy: number) => Place | undefined,
  wt: number,
): Signs | null {
  if (typeof document === 'undefined') return null;
  const rand = rng(`${seed}:signs`);
  const g = spec.grid ?? 1;

  // Every string this map needs, once.
  const strings: string[] = [];
  const slot = new Map<string, number>();
  const need = (s: string): number => {
    const key = s.toUpperCase();
    let i = slot.get(key);
    if (i === undefined) {
      i = strings.length;
      slot.set(key, i);
      strings.push(key);
    }
    return i;
  };
  for (const p of places) need(p.label);
  for (const p of places) for (const n of NOTICES[p.use] ?? []) need(n);
  need('EXIT');
  if (!strings.length) return null;

  // ---- the atlas -----------------------------------------------------------
  const COLS = 4;
  const rows = Math.ceil(strings.length / COLS);
  const CW = 512, CH = 128;
  const canvas = document.createElement('canvas');
  canvas.width = COLS * CW;
  canvas.height = Math.max(1, rows) * CH;
  const ctx = canvas.getContext('2d')!;
  /*
   * Four ways a building writes on itself.
   *
   * There used to be one: a graphite plate, two hairlines, and 600-weight
   * monospace shrunk to fit. Every notice in the game was the same object with
   * different letters in it, which is not how a place accumulates writing —
   * an enamelled door plate, a stencil sprayed on a wall, an engraved plaque
   * and a painted overhead board are four different decisions made decades
   * apart, and a street that shows all four reads as having a history.
   *
   * Style is chosen from the string itself, so a given word looks the same
   * everywhere it appears on a map and the atlas stays one cell per string.
   * Contrast is what carries a sign, so each family keeps it: the palette only
   * decides which way round it runs.
   */
  const STYLES = [
    // Enamelled plate: pale type on graphite, ruled top and bottom.
    { plate: '#1b2129', ink: '#eef3f7', font: 'ui-monospace, Menlo, monospace', weight: 600, rules: true, max: 76, track: 0 },
    // Stencil sprayed straight onto the wall: no plate to speak of.
    { plate: '#cfd5da', ink: '#20262d', font: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif', weight: 700, rules: false, max: 92, track: 4 },
    // An engraved plaque, older than the building's last refit.
    { plate: '#6c6f73', ink: '#f2efe9', font: 'ui-serif, Georgia, "Times New Roman", serif', weight: 400, rules: false, max: 68, track: 2 },
    // Overhead board, meant to be read from the far end of a concourse.
    { plate: '#12161b', ink: '#e8eef3', font: 'ui-sans-serif, "Helvetica Neue", Arial, sans-serif', weight: 800, rules: false, max: 104, track: 0 },
  ];
  ctx.fillStyle = STYLES[0].plate;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  strings.forEach((s, i) => {
    const cx = (i % COLS) * CW;
    const cy = Math.floor(i / COLS) * CH;
    let h = 2166136261;
    for (let k = 0; k < s.length; k++) { h ^= s.charCodeAt(k); h = Math.imul(h, 16777619); }
    const st = STYLES[(h >>> 7) % STYLES.length];
    ctx.fillStyle = st.plate;
    ctx.fillRect(cx + 4, cy + 4, CW - 8, CH - 8);
    ctx.fillStyle = st.ink;
    if (st.rules) {
      ctx.fillRect(cx + 10, cy + 10, CW - 20, 4);
      ctx.fillRect(cx + 10, cy + CH - 14, CW - 20, 4);
    }
    // Upper case and shrunk to fit rather than clipped: a sign that runs off
    // its own plate reads as a bug, not as a sign.
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    // `letterSpacing` is recent enough to be worth not depending on.
    const spaced = st.track ? s.split('').join(String.fromCharCode(0x2009)) : s;
    let size = st.max;
    for (; size > 18; size -= 2) {
      ctx.font = `${st.weight} ${size}px ${st.font}`;
      if (ctx.measureText(spaced).width < CW - 44) break;
    }
    ctx.fillText(spaced, cx + CW / 2, cy + CH / 2 + 2);
  });
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;

  // ---- the plates ----------------------------------------------------------
  const pos: number[] = [];
  const uv: number[] = [];
  const nrm: number[] = [];
  const idx: number[] = [];
  let tris = 0;

  const uvFor = (i: number): [number, number, number, number] => {
    const c = i % COLS;
    const r = Math.floor(i / COLS);
    const pad = 0.006;
    return [c / COLS + pad, 1 - (r + 1) / rows + pad, (c + 1) / COLS - pad, 1 - r / rows - pad];
  };

  /**
   * One sign, in the same right/up/out frame the fixtures use.
   *
   * Canonical (x, y, z) becomes Three (x, z, -y) here, once, the same as
   * everywhere else.
   */
  const plateAt = (
    x: number, y: number, z: number,
    nx: number, ny: number,
    halfWidth: number, halfHeight: number,
    which: number,
  ): void => {
    const [u0, v0, u1, v1] = uvFor(which);
    /*
     * Along the wall, from the reader's left to their right.
     *
     * The other way round is also "along the wall", and it is the one that was
     * here: the first corner took the left edge of the texture and put it on
     * the right of the reader, so every sign in the game read backwards.
     */
    const rx = -ny, ry = nx;
    const out = 0.03;
    const corner = (sr: number, su: number): [number, number, number] => [
      x + rx * sr * halfWidth + nx * out,
      y + ry * sr * halfWidth + ny * out,
      z + su * halfHeight,
    ];
    const quad: [number, number, number][] = [
      corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1),
    ];
    const uvs: [number, number][] = [[u0, v0], [u1, v0], [u1, v1], [u0, v1]];
    const start = pos.length / 3;
    const V = (p: [number, number, number]): [number, number, number] => [p[0], p[2], -p[1]];
    for (let k = 0; k < 4; k++) {
      const p = V(quad[k]);
      pos.push(p[0], p[1], p[2]);
      nrm.push(nx, 0, -ny);
      uv.push(uvs[k][0], uvs[k][1]);
    }
    // A sign is a single plate, so wound the wrong way it is not dark, it is
    // absent. Measured against the way it is meant to look, like everything
    // else here.
    const [a, b, c2] = [V(quad[0]), V(quad[1]), V(quad[2])];
    const cr = [
      (b[1] - a[1]) * (c2[2] - a[2]) - (b[2] - a[2]) * (c2[1] - a[1]),
      (b[2] - a[2]) * (c2[0] - a[0]) - (b[0] - a[0]) * (c2[2] - a[2]),
      (b[0] - a[0]) * (c2[1] - a[1]) - (b[1] - a[1]) * (c2[0] - a[0]),
    ];
    if (cr[0] * nx + cr[2] * -ny < 0) idx.push(start, start + 2, start + 1, start, start + 3, start + 2);
    else idx.push(start, start + 1, start + 2, start, start + 2, start + 3);
    tris += 2;
  };

  /*
   * Where a sign goes.
   *
   * On the wall beside a doorway, facing whoever is about to walk through it,
   * and high enough to be over a shoulder. A doorway is exactly where a person
   * looks for the name of what is on the other side, which is why signs there
   * do more for legibility than the same sign anywhere else on the wall.
   */
  for (const run of level.boundaries) {
    for (const o of run.openings) {
      if (o.kind === 'window' || o.kind === 'rappel_window') continue;
      const mid = (o.t0 + o.t1) / 2;
      for (let side = 0; side < 2; side++) {
        if (run.sides[side] === OUTSIDE || run.sides[1 - side] === OUTSIDE) continue;
        const n = side === 0 ? -1 : 1;
        const nx = run.axis === 'x' ? n : 0;
        const ny = run.axis === 'y' ? n : 0;
        const face = run.coord + n * (wt / 2);
        const x = run.axis === 'x' ? face : mid;
        const y = run.axis === 'x' ? mid : face;
        const place = placeAt(run.layer, Math.floor((x + nx * 0.5) / g), Math.floor((y + ny * 0.5) / g));
        // The sign names the room you are about to enter, so it hangs on the
        // side you are coming from and reads the far room's name.
        const other = placeAt(run.layer, Math.floor((x - nx * 0.5) / g), Math.floor((y - ny * 0.5) / g));
        if (!place || !other || other.label === place.label) continue;
        const head = Math.min(place.floor + o.head + 0.28, place.ceiling - 0.2);
        if (head - place.floor < 1.9) continue;
        plateAt(x, y, head, nx, ny, 0.9, 0.23, slot.get(other.label.toUpperCase()) ?? 0);
      }
    }
  }

  // A notice or two on a blank stretch of wall, so the walls between doors are
  // not blank stretches of wall.
  for (const place of places) {
    const notices = NOTICES[place.use] ?? [];
    if (!notices.length) continue;
    /*
     * A wall with a door in it is still a wall.
     *
     * This used to want a run with no openings at all, which on a normal plan
     * is the minority of them — the long walls, the ones you actually look at,
     * nearly all have a door somewhere along them, and every one of those was
     * left blank. The opening is a reason to place the notice elsewhere along
     * the run, not a reason to skip the run.
     */
    const runs = level.boundaries.filter(
      (r) => r.layer === place.layer && (r.sides[0] === place.space || r.sides[1] === place.space) &&
        r.emitEnd - r.emitStart > 2.4,
    );
    let n = Math.min(runs.length, 1 + Math.floor(place.area / 70));
    for (const run of runs) {
      if (n-- <= 0) break;
      const side = run.sides[0] === place.space ? 0 : 1;
      const dir = side === 0 ? -1 : 1;
      const nx = run.axis === 'x' ? dir : 0;
      const ny = run.axis === 'y' ? dir : 0;
      const face = run.coord + dir * (wt / 2);
      // Somewhere along the run that is not a doorway, and not the jamb either.
      const clear: [number, number][] = run.openings.map((o) => [o.t0 - 0.7, o.t1 + 0.7]);
      let t = run.emitStart + (0.3 + rand() * 0.4) * (run.emitEnd - run.emitStart);
      for (let tries = 0; tries < 6 && clear.some(([a, b]) => t > a && t < b); tries++) {
        t = run.emitStart + (0.1 + rand() * 0.8) * (run.emitEnd - run.emitStart);
      }
      if (clear.some(([a, b]) => t > a && t < b)) continue;
      const x = run.axis === 'x' ? face : t;
      const y = run.axis === 'x' ? t : face;
      const cx = Math.floor((x + nx * 0.5) / g);
      const cy = Math.floor((y + ny * 0.5) / g);
      if (!place.cells.has(`${cx},${cy}`)) continue;
      const word = notices[Math.floor(rand() * notices.length)];
      plateAt(x, y, place.floor + 1.9, nx, ny, 0.58, 0.15, slot.get(word) ?? 0);
    }
  }

  if (!tris) {
    tex.dispose();
    return null;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  // Unlit, and out of the tone mapper: a sign is either printed and evenly lit
  // or it is backlit, and neither of those wants the room's shadows on it.
  const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, side: THREE.FrontSide });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'signage';
  return {
    mesh,
    triangles: tris,
    dispose() {
      geo.dispose();
      mat.dispose();
      tex.dispose();
    },
  };
}
